import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, Button, Grid, IconButton, Modal, Typography } from "@mui/material";
import { Close, FileUpload } from "@mui/icons-material";
import fileUpload from "../../Assets/Icons/FileUpload.png";
import ProSelectField from "../Inputs/SelectField";
import { fieldTruePermission, prepareDefaultValues, prepareInitialConfig, convertFileToBase64Model } from "../../../utils/formHelper";
import { useForm } from "react-hook-form";
import ProButton from "../ProButton";
import {
  ADD_APPRENTICE671_FORM,
  GET_ALL_PARTNERS_DROPDOWN,
  GET_ALL_PROJECT,
  ACTIVE_APPRENTICES_BY_PROJECT_AND_PARTNER
} from "../../../utils/services/apiPath";
import { getApi, postApi } from "../../../utils/services";
import { errorToast, successToast } from "../../../utils/toastHelper";
import FileUploadBox from "./FileUploadBox";

const uploadPopupContainer = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 800,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4
};

const sixSevenOneForm = {
  projectId: {
    label: "Project Name",
    perKey: "Project",
    placeholder: "Select Project",
    rules: { required: "Project Name is Required!" }
  },
  partnerId: {
    label: "Partner Name",
    perKey: "Partner",
    placeholder: "Select Partners",
    rules: { required: "Partner Name is Required!" }
  },
  apprenticeId: {
    label: "Apprentice Name",
    perKey: "Apprentice",
    placeholder: "Select Apprentice",
    rules: { required: "Apprentice Name is Required!" }
  }
};

const UploadFileSixSevenOneForm = ({ title, getDetailData, fetchInsightData }) => {
  const [uploading, setUploading] = useState(false);
  const [formConfig] = useState(prepareInitialConfig(sixSevenOneForm, "edit"));
  const { handleSubmit, control, watch, formState, setValue } = useForm({
    defaultValues: prepareDefaultValues(sixSevenOneForm),
    mode: "onChange"
  });
  const [file, setFile] = useState(null);
  const [open, setOpen] = useState(false);
  const [projectsList, setProjectsList] = useState([]);
  const [apprenticeList, setApprenticeList] = useState([]);
  const [partnersList, setPartnersList] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const project = watch("projectId");
  const partner = watch("partnerId");
  const apprentice = watch("apprenticeId");

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleFileSelect = selectedFile => {
    setErrorMessage("");
    setFile(selectedFile);
  };

  const fetchProjects = async () => {
    const { data } = await getApi(GET_ALL_PROJECT);
    setProjectsList(data?.filter(item => item.projectId !== "All").map(item => ({ label: item.projectName, value: item.projectId })));
  };

  const fetchPartners = async () => {
    const { data } = await getApi(`${GET_ALL_PARTNERS_DROPDOWN}?projectId=${project}`);
    if (data) {
      setPartnersList(data?.filter(item => item.clientGuid !== "All").map(item => ({ label: item.companyName, value: item.clientGuid })));
    }
  };

  const fetchApprentice = async () => {
    const res = await getApi(`${ACTIVE_APPRENTICES_BY_PROJECT_AND_PARTNER}?projectId=${project}&partnerId=${partner}`);
    setApprenticeList(res?.filter(item => item.apprenticeId !== "All").map(item => ({ label: item.apprenticeName, value: item.apprenticeId })));
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (project) {
      fetchPartners();
      setValue("partnerId", null);
      setValue("apprenticeId", null);
    }
  }, [project]);

  useEffect(() => {
    if (partner) {
      fetchApprentice();
      setValue("apprenticeId", null);
    }
  }, [partner]);
  
  const defaultFormProps = { control };

  const handleSave = async () => {
    if (!file) {
      setErrorMessage("671 Form is Required!");
      return;
    }

    setUploading(true);
    try {
      const payload = {
        employeeId: apprentice,
        documentMediaModel: await convertFileToBase64Model(file)
      };
      const res = await postApi(ADD_APPRENTICE671_FORM, payload);

      if (res.error) {
        errorToast(res.error);
      } else {
        successToast("671 Form Uploaded Successfully");
        getDetailData();
        fetchInsightData();
        handleClose();
      }
    } catch (err) {
      errorToast("Something went wrong!");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <Button id="upload-files" variant="outlined" size="small" sx={{ width: "150px" }} onClick={handleOpen}>
        Upload
        <FileUpload />
      </Button>
      <Modal open={open} onClose={handleClose}>
        <Box sx={uploadPopupContainer}>
          {uploading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                bgcolor: "rgba(255,255,255,0.7)",
                zIndex: 10,
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
              }}
            >
              <Typography variant="h6">Uploading...</Typography>
            </Box>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" component="h2">
                {title}
              </Typography>
              <IconButton aria-label="close" onClick={handleClose} sx={{ position: "absolute", right: 16, top: 16 }}>
                <Close />
              </IconButton>
            </Grid>
            <ProSelectField {...defaultFormProps} {...formConfig.projectId} options={projectsList} permission={fieldTruePermission} />
            <ProSelectField {...defaultFormProps} {...formConfig.partnerId} options={partnersList} permission={fieldTruePermission} />
            <ProSelectField {...defaultFormProps} {...formConfig.apprenticeId} options={apprenticeList} permission={fieldTruePermission} />
            <Grid item xs={12}>
              <FileUploadBox onFileSelect={handleFileSelect} fileUpload={fileUpload} file={file} errorMessage={errorMessage} />
            </Grid>

            <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end", gap: "16px" }}>
              <ProButton variant="outlined" color="secondary" onClick={handleClose}>
                {"Cancel"}
              </ProButton>
              <ProButton onClick={handleSubmit(handleSave)} disabled={!formState.isValid || uploading}>
                {"Save"}
              </ProButton>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </>
  );
};

export default UploadFileSixSevenOneForm;

UploadFileSixSevenOneForm.propTypes = {
  title: PropTypes.string,
  getDetailData: PropTypes.func,
  fetchInsightData: PropTypes.func
};